import { useRef, useState } from "react";
import { Volume2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const PHONETIC_HINT = "AY-tree-ah /\u02c8e\u026a.tri.\u0259/";
const AUDIO_SRC = "/audio/aetria-pronunciation.mp3?v=2"; // cache-busted (nova HD clip)

// Plays the pronunciation of "Aetria": consistent HD clip (OpenAI tts-1-hd, voice nova,
// Whisper-verified) first; browser SpeechSynthesis only if the file fails to load. No autoplay.
export function PronounceButton({ dark = false, testid = "pronounce-aetria-btn" }) {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  const speakFallback = (done) => {
    try {
      const u = new SpeechSynthesisUtterance("Ay tree ah");
      u.rate = 0.85;
      const voices = window.speechSynthesis.getVoices();
      u.voice = voices.find((v) => /^en[-_](US|GB)/i.test(v.lang || ""))
        || voices.find((v) => (v.lang || "").startsWith("en")) || null;
      u.onend = done;
      u.onerror = done;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch {
      done();
    }
  };

  const play = () => {
    if (playing) return;
    setPlaying(true);
    const done = () => {
      clearTimeout(timerRef.current);
      setPlaying(false);
    };
    timerRef.current = setTimeout(done, 4000); // never stuck in playing state
    if (!audioRef.current) {
      audioRef.current = new Audio(AUDIO_SRC);
      audioRef.current.preload = "auto";
    }
    const audio = audioRef.current;
    audio.currentTime = 0;
    audio.onended = done;
    audio.play().catch(() => speakFallback(done));
  };

  return (
    <button type="button" onClick={play} title={PHONETIC_HINT}
      aria-label={t("common.pronounceAetria")}
      data-testid={testid} data-playing={playing ? "true" : "false"}
      className={`relative inline-flex items-center justify-center h-7 w-7 shrink-0 transition-colors ${
        dark ? "text-slate-300 hover:text-white" : "text-slate-400 hover:text-slate-600"}`}>
      {playing && (
        <>
          <span className="absolute inset-0 rounded-full border animate-ping"
            style={{ borderColor: "var(--brand-accent, #D4A017)" }} />
          <span className="absolute inset-1 rounded-full border animate-ping [animation-delay:0.25s]"
            style={{ borderColor: "var(--brand-accent, #D4A017)", opacity: 0.6 }} />
        </>
      )}
      <Volume2 className="h-4 w-4 relative"
        style={playing ? { color: "var(--brand-accent-bright, var(--brand-accent, #D4A017))" } : {}} />
    </button>
  );
}
